import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  useMediaQuery,
  Theme,
  CardContent,
  Box,
  Typography,
  Skeleton,
  Tooltip,
  IconButton,
  BoxProps,
} from '@material-ui/core';
import Icon from '../Icon';
import { IconSize, TooltipState } from '../../types';
import CardChip from './components/CardChip';
import BaseAvatar from '../Avatar';
import OneToneAvatar, { OneToneAvatarProps } from '../Avatar/OneToneAvatar';
import { CustomAvatar } from '../../types/Avatar';

interface BaseCardProps {
  avatar?: CustomAvatar;
  oneToneAvatar?: OneToneAvatarProps;
  overline?: string;
  title: string;
  subtitle?: string;
  chip?: {
    icon?: JSX.Element;
    text: string;
  };
  cardActionAreaOnClick?: () => void;
  isLoading?: boolean;
  isSelectOption?: boolean;
  dataTestIdPrefix: string;
  children?: JSX.Element | Array<JSX.Element>;
  isToDo?: boolean;
  copyIconButton?: {
    position: 'overline'; // can be expanded if this is req'd in other positions
    onClick: () => string;
  };
  sxCard?: BoxProps['sx'];
}

/**
 * Semi-smart base card, mostly built from props but accesses theme & translation to avoid extensive prop drilling
 * @param avatar - object defining the avatar props
 * @param overline - grey subtitle above main title
 * @param title - main card title
 * @param subtitle - a subtitle that sits below the main title
 * @param chip - a chip that sits below the main title
 * @param cardActionAreaOnClick - optional, if provided a CardActionArea is present with an onClick
 * @param isLoading - optional, if true a loading skeleton card is displayed
 * @param isSelectOption - optional, indicates whether it is a select option (on a list such as filters) provides a
 * leaner view if so
 * @param dataTestIdPrefix - prefix for test id's, current test id's are:
 *  - `${dataTestIdPrefix}-card` for the main Card component
 *  - `${dataTestIdPrefix}-card-action-area` for the main CardActionArea (if cardActionAreaOnClick is not undefined)
 *  - `${dataTestIdPrefix}-card-header-avatar`
 *  - `${dataTestIdPrefix}-card-header-overline`
 *  - `${dataTestIdPrefix}-card-header-title`
 *  - `${dataTestIdPrefix}-card-header-subtitle`
 *  - `${dataTestIdPrefix}-card-header-chip`
 * @param children - optional, if provided a CardContent section
 * @param sxCard - optional, styles to apply to the Card wrapper
 */
const BaseCard: React.FC<BaseCardProps> = ({
  avatar,
  oneToneAvatar,
  overline,
  title,
  subtitle,
  chip,
  cardActionAreaOnClick,
  dataTestIdPrefix = '',
  isLoading,
  isSelectOption,
  children,
  isToDo,
  copyIconButton,
  sxCard,
}: BaseCardProps) => {
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [tooltipState, setTooltipState] = useState<TooltipState>({ visible: false, message: '' });

  const cardStyles = {
    border: isSelectOption ? 'none' : '1px solid',
    borderColor: 'divider',
    ...sxCard,
  };
  const avatarSize = avatar?.size?.container || (isSmallDevice ? 48 : 64);
  const ClickArea = cardActionAreaOnClick ? CardActionArea : React.Fragment;

  const handleTooltipClose = () => {
    setTooltipState({ visible: false, message: '' });
  };

  const handleTooltipOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const message = copyIconButton.onClick() || '';
    setTooltipState({ visible: true, message });
  };

  return (
    <Card
      square={isSmallDevice ? true : false}
      elevation={0}
      component="article"
      data-testid={`${dataTestIdPrefix}-card`}
      sx={cardStyles}
    >
      <ClickArea
        {...(cardActionAreaOnClick && {
          disableRipple: true,
          onClick: cardActionAreaOnClick,
          'data-testid': `${dataTestIdPrefix}-card-action-area`,
          component: 'div',
        })}
      >
        <Box
          sx={{
            px: isSelectOption ? 0 : 4,
            py: isSelectOption ? 2 : 4,
            borderLeft: isToDo && '3px solid',
            borderColor: 'primary.main',
          }}
          data-testid="highlight"
        >
          <Box display="flex">
            {/* AVATAR */}
            {/* Improvements required: https://github.com/askporter/front-end/issues/1626 */}
            {avatar ? (
              <BaseAvatar dataTestIdPrefix={dataTestIdPrefix} altText={title} isLoading={isLoading} avatar={avatar} />
            ) : (
              !!oneToneAvatar && (
                <Box mr={4}>
                  <OneToneAvatar {...oneToneAvatar} />
                </Box>
              )
            )}
            {/* HEADER TEXT SECTIONS */}
            <Box sx={{ flex: 1, textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {overline ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    data-testid={`${dataTestIdPrefix}-card-header-overline`}
                  >
                    {isLoading ? <Skeleton variant="text" animation="wave" width={avatarSize * 1.5} /> : overline}
                  </Typography>
                  {copyIconButton?.position === 'overline' && (
                    <Tooltip
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      placement="right"
                      arrow
                      PopperProps={{
                        disablePortal: true,
                      }}
                      open={tooltipState.visible}
                      title={tooltipState.message}
                      data-testid="copy-overline-tooltip"
                    >
                      <IconButton
                        data-testid="copy-overline-button"
                        onClick={(e) => {
                          handleTooltipOpen(e);
                          setTimeout(handleTooltipClose, 1500);
                        }}
                      >
                        <Icon size={IconSize.SM} folder="actions" subfolder="05-copy" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ) : null}
              {title ? (
                <Typography
                  noWrap
                  variant={isSmallDevice || isSelectOption ? 'h6' : 'h5'}
                  data-testid={`${dataTestIdPrefix}-card-header-title`}
                >
                  {isLoading ? <Skeleton variant="text" animation="wave" width={avatarSize * 2} /> : title}
                </Typography>
              ) : null}
              {subtitle ? (
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  data-testid={`${dataTestIdPrefix}-card-header-subtitle`}
                >
                  {isLoading ? <Skeleton variant="text" animation="wave" width={avatarSize * 1.5} /> : subtitle}
                </Typography>
              ) : null}
              {chip ? (
                <>
                  {isLoading ? (
                    <Skeleton variant="rectangular" animation="wave" width={avatarSize * 2} />
                  ) : (
                    <CardChip
                      data-testid={`${dataTestIdPrefix}-card-header-chip`}
                      label={chip?.text}
                      icon={chip?.icon}
                      sx={{
                        typography: 'subtitle2',
                        py: 0.5,
                        bgcolor: isSmallDevice && !isSelectOption ? 'background.paper' : 'background.default',
                        px: isSmallDevice ? 0 : 2,
                        borderRadius: '2px',
                        '& span': { px: 0 },
                        '& img': { mr: 2 },
                      }}
                    />
                  )}
                </>
              ) : null}
            </Box>
          </Box>
          {children ? <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>{children}</CardContent> : null}
        </Box>
      </ClickArea>
    </Card>
  );
};

export default BaseCard;
